/**
 * PLAT-11059 — run a reel naming prompt and hand back the label.
 *
 * The generation path both naming stories share: this ticket names Sections with
 * it, PLAT-11064 names Chapters with it. What differs between them is the prompt
 * title and the base text, both of which are arguments — so a Chapter's naming
 * is this hook with a different record, not a second copy of it.
 *
 * It does not write. The caller owns where a name lands (`clipJson.reel`, via
 * the adapter) and what provenance it lands with, because that differs between
 * a Section and a Chapter; this returns the label or `null` and holds the
 * in-flight and failure state the rows render.
 *
 * State is keyed by target id so two rows can generate at once without one's
 * spinner or failure appearing on the other (DS6).
 */
import { useAIPromptRunLog } from '@src/monitoring/hooks/useAIPromptRunLog';
import {
  AITextGenerationError,
  generateAIText,
  PROMPT_GENERATE_ENDPOINT,
} from '@src/repositories/AITextGenerationRepository';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useRef, useState } from 'react';

import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

const logger = LogManager.getLogger('ReelEditor:useReelNameGeneration');

/** Context BY REFERENCE — the backend resolves the Brand Kit from it (DE9). */
export type ReelNameGenerationContext = {
  storyId?: string;
  showcaseId?: string;
  clipId?: string;
};

export type ReelNameGeneration = {
  /**
   * Runs the prompt over `baseText` and resolves to the label, or to `null` when
   * there was nothing to send, a generation is already in flight for this
   * target, or the run failed (in which case `errors[targetId]` says why).
   *
   * `instructions` is the creator's own steer for this one run, from the naming
   * dialog. It reaches the model as an extra line under the passage
   * (`PromptCompositionService`), so the prompt record still owns the voice and
   * the shape of the label — this only says what to name it for.
   */
  generate: (
    targetId: string,
    baseText: string,
    instructions?: string,
  ) => Promise<string | null>;
  /** Targets with a run in flight. */
  generatingIds: ReadonlySet<string>;
  /**
   * The last failure per target. It stands until the next run on that target,
   * which is also the only thing that can resolve it — a dismissal would clear
   * the one piece of evidence a creator has that the row is not simply slow
   * (PLAT-11059 DS4).
   */
  errors: Readonly<Record<string, string | undefined>>;
  /**
   * The same last failure, readable **synchronously** the moment `generate`
   * resolves — before the `errors` re-render has committed.
   *
   * The rows render `errors`; a caller that has to *report* a failure (PLAT-11213
   * fires `reel_name_generation_failed` with its reason) needs the message in the
   * continuation that saw the `null`, and waiting a render for it would mean
   * either racing the flush or firing the event from an effect, where two
   * identical failures in a row look like one.
   */
  lastError: (targetId: string) => string | undefined;
};

/**
 * `scope` is part of `PromptInputs.baseText` and its union is
 * `'clip' | 'selection'`; widening it would be a backend change DE9 rules out.
 * `composeMessages` never reads it — only `.text` reaches the model — so both
 * naming callers send `'selection'` and it has no effect on the result.
 */
const BASE_TEXT_SCOPE = 'selection' as const;

/**
 * `datoClientStore` is passed in rather than read from context, the way the
 * panel's other Dato-backed hook takes it (`useSectionSpeakers`): the Reel Editor
 * is rendered inside the clip preview with the store as a prop, and absent it
 * there is simply no prompt to resolve.
 */
export function useReelNameGeneration(
  promptTitle: string,
  context: ReelNameGenerationContext,
  datoClientStore: DatoClientStore | undefined,
): ReelNameGeneration {
  const [generatingIds, setGeneratingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  // PLAT-11207 — every run leaves an AI Prompt Log record, success or failure.
  const logPromptRun = useAIPromptRunLog();

  // Mirrored so `generate` keeps one identity for the life of the panel: it is
  // handed to memoized rows, and a fresh callback every render would defeat
  // their memo on every keystroke elsewhere in the panel.
  const contextRef = useRef(context);
  contextRef.current = context;
  const promptTitleRef = useRef(promptTitle);
  promptTitleRef.current = promptTitle;

  // Resolved once per session and held: the record's id does not change, and a
  // second GraphQL round trip per row would be paid on every generation. Its
  // type is kept alongside for the AI Prompt Log record (PLAT-11207).
  const promptIdRef = useRef<string | null>(null);
  const promptTypeRef = useRef<string | undefined>(undefined);
  // The set is also the in-flight guard, read synchronously — `generatingIds`
  // state has not committed yet when a second pick lands in the same tick.
  const inFlightRef = useRef<Set<string>>(new Set());
  // Written wherever `errors` is, for the same reason: a caller reading it right
  // after `await generate(...)` cannot see state that has not rendered yet.
  const errorsRef = useRef<Record<string, string | undefined>>({});

  const generate = useCallback(
    async (
      targetId: string,
      baseText: string,
      instructions?: string,
    ): Promise<string | null> => {
      if (!baseText.trim()) {
        // Every word in the passage was removed or cut. Nothing to characterise,
        // and an empty user message would come back as an invented label.
        logger.warn('Declining to generate a name with no words to read', {
          targetId,
        });
        return null;
      }
      if (inFlightRef.current.has(targetId)) return null;

      inFlightRef.current.add(targetId);
      setGeneratingIds(new Set(inFlightRef.current));
      errorsRef.current[targetId] = undefined;
      setErrors(previous =>
        previous[targetId] === undefined
          ? previous
          : { ...previous, [targetId]: undefined },
      );

      const title = promptTitleRef.current;
      try {
        if (!datoClientStore) {
          throw new AITextGenerationError(
            'The AI service is unavailable in this context',
          );
        }
        if (!promptIdRef.current) {
          const prompt =
            await datoClientStore.aiPromptRepository.getPromptByTitle(title);
          if (!prompt?.id) {
            throw new AITextGenerationError(
              `AI Prompt "${title}" not found in DatoCMS`,
            );
          }
          promptIdRef.current = prompt.id;
          promptTypeRef.current = prompt.promptType ?? undefined;
        }

        const promptId = promptIdRef.current;
        const options = await logPromptRun(
          {
            promptTitle: title,
            promptType: promptTypeRef.current,
            aiPromptId: promptId,
            endpoint: PROMPT_GENERATE_ENDPOINT,
            ...contextRef.current,
          },
          () =>
            generateAIText({
              promptId,
              ...contextRef.current,
              baseText,
              baseTextScope: BASE_TEXT_SCOPE,
              // Spread rather than passed as `undefined`, the shape the
              // repository itself uses for this field: a run with no steer sends
              // a request with no steer in it.
              ...(instructions ? { instructions } : {}),
            }),
        );

        // The preferred option, and only it: a picker is more interaction than a
        // one-line label deserves, and re-running is one click (DS1).
        const name = options[0]?.trim();
        if (!name) {
          throw new AITextGenerationError('The model returned no usable name');
        }
        return name;
      } catch (error) {
        logger.error(
          `Failed to generate a name from "${title}"`,
          extractErrorInfo(error),
        );
        const message =
          error instanceof Error ? error.message : 'Could not generate a name';
        errorsRef.current[targetId] = message;
        setErrors(previous => ({ ...previous, [targetId]: message }));
        return null;
      } finally {
        inFlightRef.current.delete(targetId);
        setGeneratingIds(new Set(inFlightRef.current));
      }
    },
    [datoClientStore, logPromptRun],
  );

  const lastError = useCallback(
    (targetId: string) => errorsRef.current[targetId],
    [],
  );

  return { generate, generatingIds, errors, lastError };
}
